/* Recurring Invoices index page — merchant-facing.
 *
 * Route: /billing/recurring-invoices (mounted in App.js inside the
 * authenticated shell with permission billing:invoices:view).
 *
 * Lists every RecurringInvoiceTemplate the merchant has created.
 * Each row shows customer, cadence, total, next-run date, and a
 * status badge. Row click opens a detail drawer (sub-step 7d) — for
 * this commit, click just routes to the edit modal.
 *
 * Gated behind featureId billing.recurring — FREE-tier merchants
 * see the standard FeatureGate upgrade CTA instead of the page.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, Search, Pause, Play, Send, Trash2, AlertCircle,
  RefreshCcw, Calendar
} from 'lucide-react';
import axios from '../../axiosConfig';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useCurrency } from '../../contexts/CurrencyContext';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import RecurringInvoiceModal from './RecurringInvoiceModal';
import './RecurringInvoices.css';

/* ─── Module-scope helpers + subcomponents ──────────────────── */

const STATUS_PILLS = {
  ACTIVE:    { label: 'Active',    kind: 'paid' },
  PAUSED:    { label: 'Paused',    kind: 'neutral' },
  COMPLETED: { label: 'Completed', kind: 'neutral' },
  CANCELLED: { label: 'Cancelled', kind: 'neutral' },
};

const CADENCE_LABELS = {
  DAILY: 'Daily', WEEKLY: 'Weekly', BIWEEKLY: 'Bi-weekly',
  MONTHLY: 'Monthly', QUARTERLY: 'Quarterly', YEARLY: 'Yearly',
};

const FILTERS = [
  { id: 'all',       label: 'All'       },
  { id: 'ACTIVE',    label: 'Active'    },
  { id: 'PAUSED',    label: 'Paused'    },
  { id: 'COMPLETED', label: 'Completed' },
];

function templateTotal(template, centralTax) {
  const subtotal = (template.lineItems || []).reduce((sum, li) => {
    const qty = Number(li.qty) || 0;
    const unit = Number(li.unitPrice) || 0;
    return sum + qty * unit;
  }, 0);
  const rate = centralTax?.enabled ? Number(centralTax.rate) || 0 : 0;
  const tax = Math.round((subtotal * rate) / 100 * 100) / 100;
  return subtotal + tax;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function StatusPill({ status }) {
  const cfg = STATUS_PILLS[status] || { label: status, kind: 'neutral' };
  return <span className={`rinv-pill rinv-pill--${cfg.kind}`}>{cfg.label}</span>;
}

function FilterStrip({ value, onChange }) {
  return (
    <div className="rinv-filter-strip" role="tablist" aria-label="Status filter">
      {FILTERS.map((f) => (
        <button
          key={f.id}
          type="button"
          role="tab"
          aria-selected={value === f.id}
          className={`rinv-filter-chip${value === f.id ? ' is-active' : ''}`}
          onClick={() => onChange(f.id)}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

function TemplateRow({ template, centralTax, formatMoney, onEdit, onPause, onResume, onSendNow, onDelete, busy }) {
  const total = templateTotal(template, centralTax);
  const isActive = template.status === 'ACTIVE';
  const isPaused = template.status === 'PAUSED';
  return (
    <tr className="rinv-row">
      <td className="rinv-cell rinv-cell--main">
        <button type="button" className="rinv-row-link" onClick={() => onEdit(template)}>
          <div className="rinv-customer-name">{template.customerName}</div>
          <div className="rinv-customer-email">{template.customerEmail}</div>
        </button>
      </td>
      <td className="rinv-cell" data-label="Cadence">{CADENCE_LABELS[template.cadence] || template.cadence}</td>
      <td className="rinv-cell rinv-cell--money" data-label="Total">{formatMoney(total)}</td>
      <td className="rinv-cell" data-label="Next run">{fmtDate(template.nextRunAt)}</td>
      <td className="rinv-cell" data-label="Status"><StatusPill status={template.status} /></td>
      <td className="rinv-cell rinv-cell--actions">
        {isActive && (
          <button type="button" className="rinv-icon-btn" title="Pause" onClick={() => onPause(template)} disabled={busy}>
            <Pause size={14} />
          </button>
        )}
        {isPaused && (
          <button type="button" className="rinv-icon-btn" title="Resume" onClick={() => onResume(template)} disabled={busy}>
            <Play size={14} />
          </button>
        )}
        {(isActive || isPaused) && (
          <button type="button" className="rinv-icon-btn" title="Send now" onClick={() => onSendNow(template)} disabled={busy}>
            <Send size={14} />
          </button>
        )}
        <button type="button" className="rinv-icon-btn rinv-icon-btn--danger" title="Delete" onClick={() => onDelete(template)} disabled={busy}>
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="rinv-empty">
      <Calendar size={28} />
      <h3>No recurring invoices yet</h3>
      <p>Set up a retainer schedule and let Revstar email the invoice on cadence — every week, month, quarter or year.</p>
      <button type="button" className="rinv-btn rinv-btn--primary" onClick={onNew}>
        <Plus size={14} /> Create your first template
      </button>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────── */

export default function RecurringInvoices() {
  const { isAuthenticated } = useSecureAuth();
  const toast = useToast();
  const { confirm } = useConfirm();
  const { formatInDisplayCurrency } = useCurrency();
  const formatMoney = useCallback((v) => formatInDisplayCurrency(v ?? 0), [formatInDisplayCurrency]);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('all');
  const [search, setSearch]       = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [rowBusyId, setRowBusyId] = useState(null);
  const [centralTax, setCentralTax] = useState({ enabled: false, rate: 0, name: 'VAT' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (search.trim())    params.append('q', search.trim());
      const { data } = await axios.get(`/merchant/recurring-invoices?${params.toString()}`);
      setTemplates(data?.templates || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [filter, search, toast]);

  useEffect(() => { if (isAuthenticated) load(); }, [isAuthenticated, load]);
  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    axios.get('/merchant/payment-gateways/tax-settings')
      .then(({ data }) => {
        if (alive && data?.status === 'success') setCentralTax(data.data || { enabled: false, rate: 0, name: 'VAT' });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [isAuthenticated]);

  const filteredRows = useMemo(() => templates, [templates]);

  const handleNew = useCallback(() => { setEditing(null); setModalOpen(true); }, []);
  const handleEdit = useCallback((t) => { setEditing(t); setModalOpen(true); }, []);

  const handlePause = useCallback(async (t) => {
    try {
      setRowBusyId(t.id);
      await axios.post(`/merchant/recurring-invoices/${t.id}/pause`);
      toast.success('Template paused');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Pause failed');
    } finally { setRowBusyId(null); }
  }, [load, toast]);

  const handleResume = useCallback(async (t) => {
    try {
      setRowBusyId(t.id);
      await axios.post(`/merchant/recurring-invoices/${t.id}/resume`);
      toast.success('Template resumed');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Resume failed');
    } finally { setRowBusyId(null); }
  }, [load, toast]);

  const handleSendNow = useCallback(async (t) => {
    const ok = await confirm(
      `Send an off-schedule invoice to ${t.customerName} now?`,
      { title: 'Send invoice now', confirmText: 'Send', type: 'info' },
    );
    if (!ok) return;
    try {
      setRowBusyId(t.id);
      const { data } = await axios.post(`/merchant/recurring-invoices/${t.id}/send-now`);
      toast.success(data?.emailSent ? 'Invoice sent' : 'Invoice created (email failed — resend from Invoices)');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Send failed');
    } finally { setRowBusyId(null); }
  }, [confirm, toast]);

  const handleDelete = useCallback(async (t) => {
    const ok = await confirm(
      `Delete the recurring template for ${t.customerName}? Already-issued invoices stay intact.`,
      { title: 'Delete template', confirmText: 'Delete' },
    );
    if (!ok) return;
    try {
      setRowBusyId(t.id);
      await axios.delete(`/merchant/recurring-invoices/${t.id}`);
      toast.success('Template deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Delete failed');
    } finally { setRowBusyId(null); }
  }, [confirm, load, toast]);

  const handleSaved = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
    load();
  }, [load]);

  return (
    <Shell>
      <FeatureGate
        featureId="billing.recurring"
        upgradeMessage="Recurring invoices are available on PRO and PREMIUM. Schedule retainer billing and have the customer auto-emailed."
      >
        <div className="rinv-page">
          <header className="rinv-header">
            <div className="rinv-header-text">
              <h1>Recurring Invoices</h1>
              <p>Retainer billing — schedule weekly, monthly or yearly invoices that auto-send to your customer.</p>
            </div>
            <div className="rinv-header-actions">
              <button type="button" className="rinv-btn rinv-btn--ghost" onClick={load} disabled={loading} title="Refresh">
                <RefreshCcw size={14} />
              </button>
              <button type="button" className="rinv-btn rinv-btn--primary" onClick={handleNew}>
                <Plus size={14} /> New template
              </button>
            </div>
          </header>

          <div className="rinv-controls">
            <FilterStrip value={filter} onChange={setFilter} />
            <label className="rinv-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Search customer name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          </div>

          {loading ? (
            <div className="rinv-loading">Loading…</div>
          ) : filteredRows.length === 0 ? (
            <EmptyState onNew={handleNew} />
          ) : (
            <div className="rinv-table-wrap">
              <table className="rinv-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Cadence</th>
                    <th className="rinv-th--money">Total</th>
                    <th>Next run</th>
                    <th>Status</th>
                    <th className="rinv-th--actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((t) => (
                    <TemplateRow
                      key={t.id}
                      template={t}
                      centralTax={centralTax}
                      formatMoney={formatMoney}
                      busy={rowBusyId === t.id}
                      onEdit={handleEdit}
                      onPause={handlePause}
                      onResume={handleResume}
                      onSendNow={handleSendNow}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {modalOpen && (
            <RecurringInvoiceModal
              template={editing}
              onClose={() => { setModalOpen(false); setEditing(null); }}
              onSaved={handleSaved}
            />
          )}
        </div>
      </FeatureGate>
    </Shell>
  );
}
